import { PatientSyncJobPhase } from "@elation/api-client/src/stitch-web/models/patientSyncJobPhase";
import {
  PatientSearch,
  PatientSearchProps,
} from "@src/components/data/PatientSearch";
import { FormikDateInput } from "@src/components/forms/FormikDateInput";
import { FormikInput } from "@src/components/forms/FormikInput";
import { HeaderText } from "@src/components/general/HeaderText";
import { Text } from "@src/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/components/layout/Flexbox";
import { Button } from "@src/components/ui/button";
import { Progress } from "@src/components/ui/progress";
import { usePatientFromIframe } from "@src/features/iframe";
import { NaiveDate } from "@src/utils/types";
import { Formik, useFormikContext } from "formik";
import { Search } from "lucide-react";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import * as yup from "yup";

export interface PatientSearchFormValues {
  q?: string | null;
  date_of_birth?: NaiveDate | null;
  zip_code?: string | null;
}

const validationSchema = yup.object<PatientSearchFormValues>({
  q: yup.string().nullable(true),
  date_of_birth: (yup.mixed() as yup.Schema<NaiveDate | null>).nullable(true),
  zip_code: yup.string().nullable(true),
});

export interface PatientsPageProps extends PatientSearchProps {
  patientSyncProgress: number | null;
  patientSyncPhase: PatientSyncJobPhase | null;
  onSearch: (values: PatientSearchFormValues) => void;
}

const PatientsPageForm = ({
  patientSyncProgress,
  patientSyncPhase,
}: {
  patientSyncProgress: number | null;
  patientSyncPhase: PatientSyncJobPhase | null;
}) => {
  const { values, handleSubmit } = useFormikContext<PatientSearchFormValues>();
  const [, setSearchParams] = useSearchParams();

  useEffect(() => {
    setSearchParams(
      {
        date_of_birth: values.date_of_birth
          ? NaiveDate.toString(values.date_of_birth)
          : "",
        q: values.q ?? "",
        zip_code: values.zip_code ?? "",
      },
      { replace: true },
    );
  }, [values, setSearchParams]);

  return (
    <VerticalFlexbox
      as="form"
      gap="md"
      onSubmit={handleSubmit}
      className="w-full rounded-xl border border-border bg-card p-6 shadow-soft"
    >
      <VerticalFlexbox gap="xs">
        <HeaderText tag="h1" fontWeight={600}>
          Patients
        </HeaderText>
        <Text sizing="sm" appearance="subdued">
          Search your practice roster to open a patient record.
        </Text>
        {patientSyncProgress != null ? (
          <VerticalFlexbox gap="xs" className="mt-2">
            <Text sizing="xs" appearance="subdued">
              {patientSyncPhase === "fetching-patients"
                ? "Retrieving patients from your EHR..."
                : "Your patients are being synced..."}
            </Text>
            <Progress value={patientSyncProgress} />
          </VerticalFlexbox>
        ) : null}
      </VerticalFlexbox>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
        <FormikInput
          name="q"
          label="Search"
          placeholder="Search by patient name"
        />
        <FormikDateInput name="date_of_birth" label="Date of birth" />
        <FormikInput name="zip_code" label="Zip code" />
      </div>
      <HorizontalFlexbox justifyContent="flex-end">
        <Button
          type="submit"
          className="bg-accent text-accent-foreground hover:bg-accent/90"
        >
          <Search className="mr-1 size-4" />
          Search
        </Button>
      </HorizontalFlexbox>
    </VerticalFlexbox>
  );
};

export const PatientsPage = ({
  patientSyncProgress,
  patientSyncPhase,
  patients,
  isLoading,
  isError,
  onPatientSelect,
  onSearch,
  sortBy,
  order,
  onSortChange,
  hasNextPage,
  hasPrevPage,
  onNextPage,
  onPrevPage,
}: PatientsPageProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const patientFromIframe = usePatientFromIframe();

  const dobFromSearchParams = searchParams.get("date_of_birth");

  const handleSearch = async (values: PatientSearchFormValues) => {
    onSearch(values);
    setSearchParams({
      date_of_birth: values.date_of_birth
        ? NaiveDate.toString(values.date_of_birth)
        : "",
      q: values.q ?? "",
      zip_code: values.zip_code ?? "",
    });
  };

  const iframeQ = [patientFromIframe?.first_name, patientFromIframe?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    <VerticalFlexbox gap="lg" className="w-full px-4 py-6 md:px-8 md:py-8">
      <Formik<PatientSearchFormValues>
        initialValues={{
          q: iframeQ || searchParams.get("q") || null,
          date_of_birth: patientFromIframe?.date_of_birth
            ? NaiveDate.fromString(patientFromIframe.date_of_birth)
            : dobFromSearchParams
              ? NaiveDate.fromString(dobFromSearchParams)
              : null,
          zip_code:
            patientFromIframe?.zip_code ?? searchParams.get("zip_code") ?? null,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSearch}
      >
        <PatientsPageForm
          patientSyncProgress={patientSyncProgress}
          patientSyncPhase={patientSyncPhase}
        />
      </Formik>
      <PatientSearch
        patients={patients}
        isLoading={isLoading}
        isError={isError}
        onPatientSelect={onPatientSelect}
        sortBy={sortBy}
        order={order}
        onSortChange={onSortChange}
        hasNextPage={hasNextPage}
        hasPrevPage={hasPrevPage}
        onNextPage={onNextPage}
        onPrevPage={onPrevPage}
      />
    </VerticalFlexbox>
  );
};
